/**
 * The cabinet's one identity context. Every screen in this feature reads it; nothing outside this
 * directory touches the Firebase client or `GET /v1/me`.
 *
 * WHY IT IS HAND-ROLLED RATHER THAN A QUERY LIBRARY. adr/0007-web-app-stack.md rules TanStack
 * Query, and it is NOT INSTALLED — verified against the workspace. Reported in depsNeeded. What
 * this provider does is small and unusual enough that it would need custom retry logic in any
 * case: the retry rule here is not "retry on error", it is "retry on 503 and NEVER sign out on
 * one" (adr/0010-firebase-integration.md), which is a policy decision that lives in
 * ./session-state.ts either way.
 *
 * NO CLOCK IS READ ANYWHERE IN THIS FILE. C11 bans it repo-wide ("read the injected Clock, not the
 * global"), and the back-off does not need one: the countdown is an interval that decrements a
 * number, not two instants subtracted. That is also the more honest implementation — it keeps
 * counting correctly if the machine's clock is adjusted mid-outage.
 *
 * STRICTMODE. Effects are double-invoked in development, so the subscription unsubscribes cleanly
 * and every in-flight read is guarded by a generation counter rather than by hoping it does not
 * happen twice.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import {
  becomeAuthor,
  exchangeCabinetHandoffCode,
  getAuthorSession,
  requestStreamAccess,
  requestVideoAccess,
} from '../api/authors-api';
import type { ApiResult, AuthorSession as SessionAnswer, CallOptions } from '../api/authors-api';
import { actionFor } from '../api/failure';
import type { ApiFailure } from '../api/failure';
import type { Transport } from '../api/http';
import { authClient } from '../firebase/create-auth-client';
import {
  PopupBlockedError,
  PopupCancelledError,
  ProviderUnreachableError,
} from '../firebase/auth-port';
import type { AuthClient, AuthSession, PopupProvider } from '../firebase/auth-port';
import { linkProviderKeepingAccount } from './link-anonymous';
import { onCredentialRefused } from './send-with-session';
import { initialSessionState, sessionReducer } from './session-state';
import type { SessionState } from './session-state';

/** What `/enter` got back. One member per rendered state on that screen. */
export type EnterOutcome =
  | { readonly kind: 'signed-in' }
  /** The one answer for unknown, expired AND already-used. Nothing in the body tells them apart and
   *  the screen must not pretend otherwise (docs/design/07-api/authors-api.yaml). */
  | { readonly kind: 'code-dead' }
  /** 400 — the code was missing or the wrong shape. */
  | { readonly kind: 'malformed' }
  | { readonly kind: 'rate-limited'; readonly retryAfterSeconds: number }
  /** 503. The server redeems BEFORE it mints (row 223), so the code is spent: never retried. */
  | { readonly kind: 'unavailable-code-spent' }
  /** An undeclared 5xx or no network. The request may not have redeemed the code: retried. */
  | { readonly kind: 'unavailable'; readonly retryAfterSeconds: number }
  /** The platform minted a custom token and the SDK would not accept it. */
  | { readonly kind: 'sign-in-failed' };

/** A failed link attempt: what it threw, worded where it is shown (`LinkAccountPanel`). */
export interface LinkFailure {
  readonly cause: unknown;
}

export interface AuthSessionValue {
  readonly state: SessionState;
  /** Counts down while `phase === 'reconnecting'`. `null` when nothing is scheduled. */
  readonly secondsUntilRetry: number | null;
  /** Cold start. Throws nothing: failures land in `state.signInFailure`. */
  readonly signInWithProvider: (provider: PopupProvider) => Promise<void>;
  readonly sendMagicLink: (email: string) => Promise<void>;
  readonly completeMagicLink: (email: string) => Promise<void>;
  /** True when the current URL is a magic-link callback and the handler should run. */
  readonly magicLinkCallback: boolean;
  /** The hand-off. Used by `/enter` only. */
  readonly enterWithCode: (code: string) => Promise<EnterOutcome>;
  /** G30. Adds a real provider to the anonymous account, keeping its uid. */
  readonly linkAnonymousAccount: (provider: PopupProvider) => Promise<void>;
  /** Set when a link attempt failed. Rendered; never thrown at the person. */
  readonly linkError: LinkFailure | null;
  /** Re-reads `GET /v1/me` now, cancelling any pending back-off. */
  readonly retryNow: () => void;
  /** Local only — the cabinet holds no server-side session and this surface has no sign-out route. */
  readonly signOut: () => Promise<void>;
  /**
   * The terms interstitial is on screen. The `/terms` route calls it in a live build, so the gate
   * puts the interstitial in front of a cabinet screen at most once per visit
   * (`termsInterstitialDue` in ./session-state.ts).
   */
  readonly noteTermsInterstitialShown: () => void;
  /**
   * "Become an author" (design 25, P16): `POST /v1/authorship` for whoever is signed in. Its answer
   * is the session, taken at once - the reader is an applicant, and the cabinet opens to them. A
   * refusal is handed back and changes nothing, except a credential refused for good, which ends
   * the session as a screen's own 401 does (`credential-refused`).
   */
  readonly becomeAuthor: () => Promise<ApiResult<SessionAnswer>>;
  /**
   * "Request access to video" (v1.0.29): `POST /v1/video-access`. Its answer is the session, taken
   * at once, so `videoAccess` reads `requested` everywhere; a refusal is handed back as above.
   */
  readonly requestVideoAccess: () => Promise<ApiResult<SessionAnswer>>;
  /** "Request access to live streams" (1.2.0): `POST /v1/stream-access`, taken as the one above. */
  readonly requestStreamAccess: () => Promise<ApiResult<SessionAnswer>>;
}

const AuthSessionContext = createContext<AuthSessionValue | null>(null);

export interface AuthSessionProviderProps {
  readonly children: ReactNode;
  /** Injected by tests. Production uses the one seam in ../firebase/create-auth-client.ts. */
  readonly client?: AuthClient;
  /**
   * Injected by tests, so a spec can answer `GET /v1/me` with a session the fixture has no scenario
   * for. Production uses the one seam in ../api/transport.ts.
   */
  readonly transport?: Transport;
}

function signInFailureFor(
  error: unknown,
): 'cancelled' | 'popup-blocked' | 'provider-unreachable' | 'unknown' {
  if (error instanceof PopupBlockedError) {
    return 'popup-blocked';
  }
  if (error instanceof PopupCancelledError) {
    return 'cancelled';
  }
  if (error instanceof ProviderUnreachableError) {
    return 'provider-unreachable';
  }
  return 'unknown';
}

function outcomeForExchangeFailure(failure: ApiFailure): EnterOutcome {
  switch (failure.kind) {
    case 'unauthenticated':
      // THE ONE ANSWER. On this operation a 401 says nothing about the caller's session — it says
      // the code is dead — so nobody is signed out here.
      return { kind: 'code-dead' };
    case 'refused':
      return failure.status === 400 ? { kind: 'malformed' } : { kind: 'code-dead' };
    case 'rate-limited':
      return { kind: 'rate-limited', retryAfterSeconds: failure.retryAfterSeconds };
    case 'identity-unavailable':
      return { kind: 'unavailable-code-spent' };
    case 'unexpected':
      return { kind: 'unavailable', retryAfterSeconds: failure.retryAfterSeconds };
    case 'unreachable':
      return { kind: 'unavailable', retryAfterSeconds: 0 };
  }
}

export function AuthSessionProvider({ children, client, transport }: AuthSessionProviderProps) {
  const [state, dispatch] = useReducer(sessionReducer, initialSessionState);
  const [secondsUntilRetry, setSecondsUntilRetry] = useState<number | null>(null);
  const [linkError, setLinkError] = useState<LinkFailure | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);

  // Resolved once. A second AuthClient would mean a second observer set, and the second one never
  // fires.
  const clientRef = useRef<AuthClient | null>(null);
  if (clientRef.current === null) {
    clientRef.current = client ?? authClient();
  }
  const auth = clientRef.current;
  // Resolved once, like the client. Empty in production: the operations then use their own seam.
  const callOptions = useRef<CallOptions>(transport === undefined ? {} : { transport }).current;

  /** The LIVE session object, which is the only thing that can produce a bearer token. */
  const liveSession = useRef<AuthSession | null>(null);
  /** Rises on every identity change, so a slow read from a previous identity is discarded. */
  const generation = useRef(0);

  const [magicLinkCallback, setMagicLinkCallback] = useState(false);
  useEffect(() => {
    setMagicLinkCallback(auth.isMagicLinkUrl(window.location.href));
  }, [auth]);

  useEffect(() => {
    const unsubscribe = auth.subscribe((next) => {
      liveSession.current = next;
      generation.current += 1;
      dispatch({ type: 'identity-changed', identity: next?.identity ?? null });
    });
    return unsubscribe;
  }, [auth]);

  /**
   * `GET /v1/me`, with the ONE forced token refresh the ruled 401 split allows.
   *
   * A bare `Bearer realm="moonx"` 401 means no credential was presented, which can be our own
   * staleness, so it is worth exactly one refresh. A 401 carrying `error="invalid_token"` is the
   * challenge that "stops a client retrying a token that will never work" — no refresh, sign out.
   */
  const loadSession = useCallback(async (forceRefresh: boolean): Promise<void> => {
    const live = liveSession.current;
    if (live === null) {
      return;
    }
    const mine = generation.current;
    dispatch({ type: 'session-requested' });

    let token: string;
    try {
      token = await live.getIdToken(forceRefresh);
    } catch {
      // The SDK could not produce a token. That is a "cannot verify right now", not a
      // "your token is invalid", so it holds the session exactly like a 503 does.
      if (generation.current === mine) {
        dispatch({ type: 'session-failed', failure: { kind: 'unreachable' } });
      }
      return;
    }

    const result = await getAuthorSession(token, callOptions);
    if (generation.current !== mine) {
      return;
    }

    if (result.ok) {
      dispatch({ type: 'session-loaded', session: result.value });
      return;
    }

    if (actionFor(result.failure) === 'refresh-token-once' && !forceRefresh) {
      dispatch({ type: 'token-refresh-attempted' });
      await loadSession(true);
      return;
    }

    dispatch({ type: 'session-failed', failure: result.failure });
  }, []);

  /**
   * A SCREEN'S OWN REQUEST WHOSE CREDENTIAL WAS REFUSED, reported by ./send-with-session.ts after its
   * one forced refresh, or with the challenge that forbids one. It is applied only to the person the
   * refused token belonged to - a slow request from before an account switch must not end the
   * session of whoever is signed in now - and the generation rises first, so a `GET /v1/me` still in
   * flight cannot land `ready` on top of the sign-out. The reducer decides the rest.
   */
  useEffect(
    () =>
      onCredentialRefused((refusal) => {
        if (refusal.uid === null || refusal.uid !== liveSession.current?.identity.uid) {
          return;
        }
        generation.current += 1;
        dispatch({ type: 'credential-refused', failure: refusal.failure });
      }),
    [],
  );

  // KEYED ON THE IDENTITY OBJECT, NOT ITS UID. A G30 link keeps the uid and changes the providers,
  // and the reducer answers every `identity-changed` with `loading-session` - so a uid key never
  // re-read `GET /v1/me` after a link and the screen stayed on its loading state. The reducer makes
  // a new identity object only on `identity-changed`, and the Firebase adapter reports only real
  // identity changes (firebase-auth-client.ts), so this re-reads once per sign-in or link and never
  // on a token refresh.
  const identity = state.identity;
  useEffect(() => {
    if (identity === null) {
      return;
    }
    void loadSession(false);
  }, [identity, reloadNonce, loadSession]);

  /**
   * ★ A FAILURE THAT ENDS THE SESSION ENDS IT IN THE SDK TOO. `applyFailure` (session-state.ts)
   * resets the identity to null on an `invalid_token` challenge or a second bare 401, while the SDK
   * still holds the user - and the Firebase adapter forwards only REAL identity changes, so signing
   * back in as the same person would report nothing and leave them on `/sign-in`. "Identity null
   * while the SDK still has a session" is that state and no other: `sign-in-cancelled` and
   * `sign-in-failed` keep the identity (a dead hand-off code signs NOBODY out), and an explicit
   * sign-out has already run `auth.signOut()` before its `signed-out` lands.
   */
  useEffect(() => {
    if (state.phase === 'signed-out' && identity === null && liveSession.current !== null) {
      void auth.signOut();
    }
  }, [state.phase, identity, auth]);

  const retryNow = useCallback(() => {
    setSecondsUntilRetry(null);
    setReloadNonce((n) => n + 1);
  }, []);

  /**
   * THE BACK-OFF. An interval that counts a number down, never two instants subtracted — see the
   * clock note at the top of this file. When it reaches zero the read is retried; the SESSION IS
   * NEVER TOUCHED, which is the whole reason `reconnecting` is a phase and not a sign-out.
   */
  const reconnecting = state.phase === 'reconnecting';
  const scheduledSeconds = state.retryAfterSeconds;
  useEffect(() => {
    if (!reconnecting) {
      setSecondsUntilRetry(null);
      return;
    }
    setSecondsUntilRetry(scheduledSeconds ?? 0);

    const timer = window.setInterval(() => {
      setSecondsUntilRetry((remaining) => {
        if (remaining === null) {
          return null;
        }
        if (remaining <= 1) {
          window.clearInterval(timer);
          setReloadNonce((n) => n + 1);
          return null;
        }
        return remaining - 1;
      });
    }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [reconnecting, scheduledSeconds]);

  const signInWithProvider = useCallback(
    async (provider: PopupProvider): Promise<void> => {
      dispatch({ type: 'sign-in-started' });
      try {
        await auth.signInWithPopup(provider);
        // The subscription reports the new identity; nothing more to do here.
      } catch (error) {
        const reason = signInFailureFor(error);
        if (reason === 'cancelled') {
          dispatch({ type: 'sign-in-cancelled' });
          return;
        }
        dispatch({ type: 'sign-in-failed', reason });
      }
    },
    [auth],
  );

  const sendMagicLink = useCallback(
    async (email: string): Promise<void> => {
      await auth.sendMagicLink(email);
    },
    [auth],
  );

  const completeMagicLink = useCallback(
    async (email: string): Promise<void> => {
      dispatch({ type: 'sign-in-started' });
      try {
        await auth.completeMagicLink(window.location.href, email);
      } catch (error) {
        const reason = signInFailureFor(error);
        // There is no popup to cancel on this path, so a "cancelled" from the adapter is not a
        // meaningful state here — it is an unclassified failure.
        dispatch({ type: 'sign-in-failed', reason: reason === 'cancelled' ? 'unknown' : reason });
      }
    },
    [auth],
  );

  const enterWithCode = useCallback(
    async (code: string): Promise<EnterOutcome> => {
      dispatch({ type: 'sign-in-started' });
      const exchanged = await exchangeCabinetHandoffCode(code, callOptions);
      if (!exchanged.ok) {
        const outcome = outcomeForExchangeFailure(exchanged.failure);
        // NOBODY IS SIGNED OUT BY A DEAD CODE. The person may well have a perfectly good session
        // on this origin already; the code failing says nothing about it.
        dispatch({ type: 'sign-in-cancelled' });
        return outcome;
      }

      try {
        await auth.signInWithCustomToken(exchanged.value.customToken);
      } catch {
        dispatch({ type: 'sign-in-failed', reason: 'unknown' });
        return { kind: 'sign-in-failed' };
      }
      return { kind: 'signed-in' };
    },
    [auth],
  );

  const linkAnonymousAccount = useCallback(
    async (provider: PopupProvider): Promise<void> => {
      setLinkError(null);
      try {
        await linkProviderKeepingAccount(auth, provider);
      } catch (error) {
        if (error instanceof PopupCancelledError) {
          return;
        }
        setLinkError({ cause: error });
      }
    },
    [auth],
  );

  const signOut = useCallback(async (): Promise<void> => {
    await auth.signOut();
    dispatch({ type: 'signed-out' });
  }, [auth]);

  const noteTermsInterstitialShown = useCallback(() => {
    dispatch({ type: 'terms-interstitial-shown' });
  }, []);

  /**
   * A write whose answer is the session - `becomeAuthor`, `requestVideoAccess` - taken at once.
   * ONE FORCED REFRESH, as `GET /v1/me` has (`loadSession`): a bare 401 may be our own staleness.
   * The answer is applied only to the person it was asked for - the generation, as above.
   */
  const answeredWithSession = useCallback(
    async (
      operation: (bearerToken: string, options: CallOptions) => Promise<ApiResult<SessionAnswer>>,
    ): Promise<ApiResult<SessionAnswer>> => {
      const live = liveSession.current;
      if (live === null) {
        return { ok: false, failure: NOBODY_SIGNED_IN };
      }
      const mine = generation.current;
      const send = async (forceRefresh: boolean) =>
        operation(await live.getIdToken(forceRefresh), callOptions);
      let result: ApiResult<SessionAnswer>;
      try {
        result = await send(false);
        if (!result.ok && actionFor(result.failure) === 'refresh-token-once') {
          result = await send(true);
        }
      } catch {
        // The SDK could not produce a token: "cannot verify right now", never a sign-out.
        return { ok: false, failure: { kind: 'unreachable' } };
      }
      if (generation.current !== mine) {
        return result;
      }
      if (result.ok) {
        dispatch({ type: 'session-loaded', session: result.value });
      } else if (result.failure.kind === 'unauthenticated') {
        generation.current += 1;
        dispatch({ type: 'credential-refused', failure: result.failure });
      }
      return result;
    },
    [],
  );
  const becomeAuthorNow = useCallback(
    () => answeredWithSession(becomeAuthor),
    [answeredWithSession],
  );
  const requestVideoAccessNow = useCallback(
    () => answeredWithSession(requestVideoAccess),
    [answeredWithSession],
  );
  const requestStreamAccessNow = useCallback(
    () => answeredWithSession(requestStreamAccess),
    [answeredWithSession],
  );

  const value = useMemo<AuthSessionValue>(
    () => ({
      state,
      secondsUntilRetry,
      signInWithProvider,
      sendMagicLink,
      completeMagicLink,
      magicLinkCallback,
      enterWithCode,
      linkAnonymousAccount,
      linkError,
      retryNow,
      signOut,
      noteTermsInterstitialShown,
      becomeAuthor: becomeAuthorNow,
      requestVideoAccess: requestVideoAccessNow,
      requestStreamAccess: requestStreamAccessNow,
    }),
    [
      state,
      secondsUntilRetry,
      signInWithProvider,
      sendMagicLink,
      completeMagicLink,
      magicLinkCallback,
      enterWithCode,
      linkAnonymousAccount,
      linkError,
      retryNow,
      signOut,
      noteTermsInterstitialShown,
      becomeAuthorNow,
      requestVideoAccessNow,
      requestStreamAccessNow,
    ],
  );

  return <AuthSessionContext.Provider value={value}>{children}</AuthSessionContext.Provider>;
}

/**
 * Reads the context, and refuses to guess when it is missing. A hook that silently returned a
 * signed-out value outside the provider would render the sign-in screen inside the cabinet and
 * look like a session bug.
 */
export function useAuthSession(): AuthSessionValue {
  const value = useContext(AuthSessionContext);
  if (value === null) {
    throw new Error('useAuthSession was called outside <AuthSessionProvider>.');
  }
  return value;
}

/**
 * The context, or `null` outside the provider - for a screen that only ADJUSTS to the session and
 * is also mounted with none around it: the editor puts "Your author application is approved first"
 * where Submit would be for an applicant (design 25), and its specs mount it bare. Anything that
 * needs the session uses `useAuthSession`, which refuses to guess.
 */
export function useOptionalAuthSession(): AuthSessionValue | null {
  return useContext(AuthSessionContext);
}

/** `becomeAuthor` with nobody signed in: the 401 the operation would answer, without asking it. */
const NOBODY_SIGNED_IN = {
  kind: 'unauthenticated',
  tokenPresentedAndFailed: false,
  errorCode: null,
  problem: null,
} as const satisfies ApiFailure;
